import BreadCrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import DeleteModal from '@components/DeleteModal';
import Icon from '@material-ui/core/Icon';
import Alert from '@material-ui/lab/Alert';
import { apiClient } from '@services/api';
import { useEffect, useState, useRef } from 'react';
import NumberFormat from 'react-number-format';
import { Link, useHistory, useParams, useLocation } from 'react-router-dom';
import RequestTable from './partials/RequestTable';
import PaymentsDetail from './partials/PaymentsDetail';
import SendVoucherModal from './partials/SendVoucherModal';

/* ── paleta ────────────────────────────────── */
const C = {
  bg:        '#0f1e35',
  card:      '#172B4D',
  hover:     '#1e3a5a',
  accent:    '#62DEC2',
  textSec:   '#8ba5c4',
  textMain:  '#e8f0fb',
  btnDef:    '#2a4f72',
};

/* ── badge do status do cliente ─────────────── */
const CustomerStatusBadge = ({ presales }) => {
  const today = new Date();
  const expiresAt = presales?.expires_at ? new Date(presales.expires_at) : null;
  const isExpired = expiresAt && expiresAt < today;

  if (presales?.customer_status === 'approved') {
    return (
      <span style={{
        background: '#166534',
        color: '#bbf7d0',
        border: '1px solid #22c55e',
        borderRadius: 8,
        padding: '4px 12px',
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <Icon style={{ fontSize: 16 }}>check_circle</Icon>
        Cliente aprovou ✓
      </span>
    );
  }

  if (presales?.customer_status === 'refused') {
    return (
      <span style={{
        background: '#7f1d1d',
        color: '#fecaca',
        border: '1px solid #ef4444',
        borderRadius: 8,
        padding: '4px 12px',
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <Icon style={{ fontSize: 16 }}>cancel</Icon>
        Cliente recusou
        {presales?.customer_refusal_reason && (
          <span style={{ fontWeight: 400, fontSize: 12 }}>
            — {presales.customer_refusal_reason}
          </span>
        )}
      </span>
    );
  }

  if (isExpired) {
    return (
      <span style={{
        background: '#78350f',
        color: '#fde68a',
        border: '1px solid #f59e0b',
        borderRadius: 8,
        padding: '4px 12px',
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <Icon style={{ fontSize: 16 }}>schedule</Icon>
        Expirado
      </span>
    );
  }

  return null;
};

/* ── thread de comentários ──────────────────── */
const CommentsThread = ({ presalesId }) => {
  const [comments, setComments]   = useState([]);
  const [message, setMessage]     = useState('');
  const [sending, setSending]     = useState(false);
  const [loadError, setLoadError] = useState(false);
  const bottomRef = useRef(null);

  const loadComments = () => {
    apiClient.httpInstance
      .get(`/presales/${presalesId}/comments`)
      .then(res => {
        if (res.status === 200) setComments(res.data);
      })
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    loadComments();
    const id = setInterval(loadComments, 30000);
    return () => clearInterval(id);
  }, [presalesId]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  const handleSend = () => {
    if (!message.trim()) return;
    setSending(true);
    apiClient.httpInstance
      .post(`/presales/${presalesId}/comments`, { message: message.trim() })
      .then(res => {
        if (res.status === 200 || res.status === 201) {
          setMessage('');
          loadComments();
        }
      })
      .catch(() => {})
      .finally(() => setSending(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ marginTop: 24, marginBottom: 24 }}>
      <div style={{ color: C.accent, fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
        Mensagens com o cliente
      </div>
      <div style={{
        background: C.card,
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxHeight: 420,
        overflowY: 'auto',
      }}>
        {loadError && (
          <div style={{ color: C.textSec, fontSize: 13, textAlign: 'center' }}>
            Não foi possível carregar os comentários.
          </div>
        )}
        {!loadError && comments.length === 0 && (
          <div style={{ color: C.textSec, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            Nenhuma mensagem ainda.
          </div>
        )}
        {comments.map((c) => {
          const isConsultor = c.author_type === 'consultant' || c.author_type === 'user';
          return (
            <div
              key={c.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isConsultor ? 'flex-end' : 'flex-start',
              }}
            >
              <div style={{ color: C.textSec, fontSize: 11, marginBottom: 3 }}>
                {c.author_name}
              </div>
              <div style={{
                background: isConsultor ? C.btnDef : C.hover,
                color: C.textMain,
                borderRadius: 12,
                padding: '8px 12px',
                maxWidth: '70%',
                wordBreak: 'break-word',
                fontSize: 14,
              }}>
                {c.message}
              </div>
              <div style={{ color: C.textSec, fontSize: 10, marginTop: 3 }}>
                {formatDate(c.created_at)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input para envio */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'flex-end' }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Digite uma mensagem..."
          rows={2}
          style={{
            flex: 1,
            background: C.card,
            border: `1px solid ${C.hover}`,
            borderRadius: 8,
            color: C.textMain,
            padding: '10px 12px',
            fontSize: 14,
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
        <button
          onClick={handleSend}
          disabled={sending || !message.trim()}
          style={{
            background: sending || !message.trim() ? '#1e3a5a' : C.btnDef,
            color: C.textMain,
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            cursor: sending || !message.trim() ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'background 0.2s',
          }}
        >
          <Icon style={{ fontSize: 18 }}>send</Icon>
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  );
};

/* ── componente principal ───────────────────── */
const Show = (props) => {
  document.title = `Detalhes do orçamento - ${process.env.REACT_APP_NAME}`
  const _location = useLocation();
  const approve = _location.state?.approve || false;
  const approvalSent = new URLSearchParams(_location.search).get('approval') === 'sent';
  const { presales_id } = useParams();
  const [presales, setPreSale] = useState({});
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendVoucherModal, setSendVoucherModal] = useState(false);
  const [disableModal, setDisableModal] = useState(false)
  const [urlLink, setUrlLink] = useState(null)
  // Modal de checagem de datas antes da conversão
  const [dateCheckModal, setDateCheckModal] = useState(false)
  const [dateIssues, setDateIssues]         = useState([])
  const [fixDates, setFixDates]             = useState({}) // { budget_id: 'YYYY-MM-DD' }
  const [fixFlightCodes, setFixFlightCodes] = useState({}) // { budget_id: 'LA123' }
  const [fixFlightTimes, setFixFlightTimes] = useState({}) // { budget_id: '10:30' }
  const [fixAvailDates, setFixAvailDates]   = useState({}) // { budget_id: [{ cupo_date, total_cupos }] }
  const [converting, setConverting]         = useState(false)
  const [sendEmailModal, setSendEmailModal] = useState(false)
  const [sendEmailAddress, setSendEmailAddress] = useState('')
  const [sendEmailLoading, setSendEmailLoading] = useState(false)
  const [sendEmailSuccess, setSendEmailSuccess] = useState(false)
  // Gerente — modal de aprovação gerencial
  const [approvalModal,        setApprovalModal]        = useState(false);
  const [approvalManagerValue, setApprovalManagerValue] = useState('');
  const [approvalManagerNote,  setApprovalManagerNote]  = useState('');
  const [approvalSubmitting,   setApprovalSubmitting]   = useState(false);
  const [approvalError,        setApprovalError]        = useState(null);
  const [refuseModal,          setRefuseModal]          = useState(false);
  const [refuseNote,           setRefuseNote]           = useState('');
  const history = useHistory();
  const url = `/presales/${presales_id}`

  const getSalesDetails = url => {
    apiClient.httpInstance.get(url).then(response => {
      if (response.status === 200) {
        setPreSale(response.data);
        if (response.data.url_link) {
          setUrlLink(response.data.url_link)
        } else {
          getClientById(response.data.customer_id)
        }
        setLoading(false);
      }
    })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage("Esse orçamento não existe");
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
          setLoading(false);
        }
      });
  }

  const getClientById = (id) => {
    apiClient.httpInstance.get(`customers/${id}`).then(response => {
      if (response.status === 200) {
        setUrlLink(response.data.url_link)
        setLoading(false);
      }
    })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage("Cliente não encontrado");
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  const getBudgets = url => {
    apiClient.httpInstance.get(`${url}/budgets`).then(response => {
      if (response.status === 200) {
        const sorted = (response.data || []).slice().sort((a, b) => {
          const ta = a.tour_date ? new Date(a.tour_date + 'T00:00:00').getTime() : Infinity;
          const tb = b.tour_date ? new Date(b.tour_date + 'T00:00:00').getTime() : Infinity;
          return ta - tb;
        });
        setBudgets(sorted);
        setLoading(false)
      }
    }).catch(error => {
      if (error) {
        setError(true);
        setErrorMessage("Erro inesperado, tente novamente mais tarde")
        setTimeout(() => { setError(false) }, 4000);
        setTimeout(() => { setErrorMessage(null) }, 4000);
      }
    });
  }

  const cancelPresale = url => {
    apiClient.httpInstance.put(url)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/orcamentos`);
        }
      })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage(error?.response?.data?.message || error?.message || "Erro inesperado, tente novamente mais tarde")
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  const approvePresale = () => {
    apiClient.httpInstance.post(`/presales/${presales_id}/approve`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/aprovacoes`);
        }
      })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage(error?.response?.data?.message || error?.message || "Erro inesperado, tente novamente mais tarde")
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  const disaprovePresale = () => {
    apiClient.httpInstance.post(`/presales/${presales_id}/disapprove`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/aprovacoes`);
        }
      })
      .catch(error => {
        if (error) {
          setError(true);
          setErrorMessage(error?.response?.data?.message || error?.message || "Erro inesperado, tente novamente mais tarde")
          setTimeout(() => { setError(false) }, 4000);
          setTimeout(() => { setErrorMessage(null) }, 4000);
        }
      });
  }

  /* ── helpers aprovação gerencial ── */
  const approvalTypeLabel = (type, value) => {
    if (!type || !value) return '—';
    if (type === 'discount_pct')   return `${value}% de desconto`;
    if (type === 'discount_value') return `R$ ${value} de desconto`;
    if (type === 'installment')    return `${value}x sem juros`;
    return value;
  };

  const doApproveApproval = () => {
    if (!approvalManagerValue.trim()) return;
    setApprovalSubmitting(true);
    setApprovalError(null);
    apiClient.httpInstance
      .post(`/presales/${presales_id}/approval/approve`, {
        approval_manager_value: approvalManagerValue.trim(),
        approval_manager_note:  approvalManagerNote.trim() || undefined,
      })
      .then(() => history.push('/admin/aprovacoes'))
      .catch(err => {
        setApprovalError(err?.response?.data?.error || 'Erro ao aprovar.');
        setApprovalSubmitting(false);
      });
  };

  const doRefuseApproval = () => {
    setApprovalSubmitting(true);
    apiClient.httpInstance
      .post(`/presales/${presales_id}/approval/refuse`, {
        approval_manager_note: refuseNote.trim() || undefined,
      })
      .then(() => history.push('/admin/aprovacoes'))
      .catch(() => setApprovalSubmitting(false));
  };

  // Abre modal de reenvio de email com email do cliente pré-preenchido
  const openSendEmailModal = () => {
    setSendEmailAddress(presales?.email ?? '')
    setSendEmailSuccess(false)
    setSendEmailModal(true)
  }

  // Envia email do orçamento para o endereço informado
  const handleSendEmail = () => {
    setSendEmailLoading(true)
    apiClient.httpInstance
      .post(`/presales/${presales_id}/send_document`, { email: sendEmailAddress })
      .then(() => {
        setSendEmailSuccess(true)
        setSendEmailLoading(false)
        setTimeout(() => setSendEmailModal(false), 1800)
      })
      .catch(() => {
        setSendEmailLoading(false)
        alert('Erro ao enviar e-mail. Tente novamente.')
      })
  }

  // Etapa 1: checar datas antes de converter
  const saleConvert = (presaleId) => {
    apiClient.httpInstance.get(`/presales/${presaleId}/check-dates`)
      .then(response => {
        const { ok, issues } = response.data;
        if (ok) {
          doConvert(presaleId);
        } else {
          // Inicializa fixDates com as datas atuais dos itens com problemas
          const initial = {};
          const initFlightCodes = {};
          const initFlightTimes = {};
          issues.forEach(i => {
            initial[i.budget_id] = i.tour_date || '';
            if (i.is_transfer) {
              initFlightCodes[i.budget_id] = i.flight_code || '';
              initFlightTimes[i.budget_id] = i.flight_time ? i.flight_time.substring(0, 5) : '';
            }
          });
          setFixDates(initial);
          setFixFlightCodes(initFlightCodes);
          setFixFlightTimes(initFlightTimes);
          setDateIssues(issues);
          // Buscar datas disponíveis apenas para tours (transfers não têm cupos)
          issues.filter(i => !i.is_transfer).forEach(issue => {
            apiClient.httpInstance.get(`reservations_dates/available?tour_id=${issue.tour_id}`)
              .then(r => {
                setFixAvailDates(prev => ({ ...prev, [issue.budget_id]: r.data || [] }));
              })
              .catch(() => {});
          });
          setDateCheckModal(true);
        }
      })
      .catch(() => {
        setError(true);
        setErrorMessage('Não foi possível verificar disponibilidade de vagas. Tente novamente antes de converter.');
        setTimeout(() => setError(false), 5000);
        setTimeout(() => setErrorMessage(null), 5000);
      });
  };

  // Etapa 2: salvar datas corrigidas e reconverter
  const saveFixedDatesAndConvert = () => {
    setConverting(true);
    const saves = dateIssues.map(issue => {
      const payload = { date: fixDates[issue.budget_id] };
      if (issue.is_transfer) {
        payload.flight_code = fixFlightCodes[issue.budget_id] || '';
        payload.flight_time = fixFlightTimes[issue.budget_id]
          ? fixFlightTimes[issue.budget_id] + ':00'
          : '';
      }
      return apiClient.httpInstance.put(
        `/presales/${presales_id}/budgets/${issue.budget_id}/date`,
        payload
      );
    });
    Promise.all(saves)
      .then(() => {
        setDateCheckModal(false);
        doConvert(presales_id);
      })
      .catch(() => {
        setConverting(false);
        setError(true);
        setErrorMessage('Erro ao salvar datas. Tente novamente.');
        setTimeout(() => setError(false), 4000);
      });
  };

  // Converte de fato
  const doConvert = (presaleId) => {
    apiClient.httpInstance.post(`/presales/${presaleId}/convert-in-sale`)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          history.push(`/admin/carrinho`);
        }
      })
      .catch(error => {
        setConverting(false);
        setError(true);
        setErrorMessage(error?.response?.data?.message || "Erro inesperado, tente novamente mais tarde");
        setTimeout(() => { setError(false) }, 4000);
        setTimeout(() => { setErrorMessage(null) }, 4000);
      });
  };

  const bradecrumbsConfig = [
    {
      title: `Início`,
      active: true,
      route: approve ? '/admin/aprovacoes' : '/admin/orcamentos'
    },
    {
      title: `Orçamento ${presales.references || ''}`,
    }
  ]

  const status = {
    pending: {
      text: 'Aguardando aprovação',
      icon: 'access_time'
    },
    refused: {
      text: 'Negado',
      icon: 'running_with_errors'
    },
    overdue: {
      text: 'Vencido',
      icon: 'running_with_errors'
    },
    open: {
      text: 'Em aberto',
      icon: 'access_time'
    },
    approved: {
      text: 'Aprovado',
      icon: 'check_circle_outline'
    },
    sold: {
      text: 'Convertido em venda',
      icon: 'access_time'
    },
    canceled: {
      text: 'Cancelado',
      icon: 'access_time'
    },
  }

  useEffect(() => {
    getSalesDetails(url);
    getBudgets(url);
  }, [url]);

  const cardStyle = {
    background: C.card,
    borderRadius: 10,
    marginTop: 16,
    color: C.textMain,
  };

  const cardHeaderStyle = {
    padding: '16px',
    fontSize: 18,
    color: C.textMain,
    borderBottom: `1px solid ${C.hover}`,
  };

  return <>
    {!loading &&
      <>
        {error && (
          <Alert className="mb-5" variant="filled" severity="error">
            {errorMessage}
          </Alert>
        )}
        <BreadCrumbs items={bradecrumbsConfig} />

        {/* Banner: pedido gerencial enviado com sucesso (consultor) */}
        {approvalSent && (
          <div style={{
            margin: '12px 28px 0',
            background: '#052e16', border: '1px solid #22c55e',
            borderRadius: 10, padding: '12px 18px',
            color: '#86efac', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon style={{ fontSize: 20 }}>check_circle</Icon>
            Pedido gerencial enviado! Aguardando aprovação do gerente.
          </div>
        )}

        <div className="px-7 pt-6">
          {/* Título + badge status cliente + botões de ação */}
          <div className="flex items-center justify-between mt-2 text-2xl" style={{ color: C.textMain }}>
            <div className="flex items-center gap-4 flex-wrap">
              <span>Orçamento {presales.references || ''}</span>
              <CustomerStatusBadge presales={presales} />
            </div>
            {!approve && <div className="w-100">
              <Button
                textcolor="#fff"
                padding="6px 15px"
                margintop="0px"
                marginright="20px"
                width="100px"
                onClick={() => history.push(`/admin/orcamentos/editar/${presales_id}`)}
                customcolor={props.theme.color}
                disabled={presales?.status != 'canceled' ? false : true}
              >
                <Icon className="mr-2">
                  edit
                </Icon>
                Editar
              </Button>
              <Button
                textcolor="#fff"
                padding="6px 15px"
                margintop="0px"
                marginright="20px"
                width="250px"
                onClick={() => setDisableModal(true)}
                bgColor="#F44336"
                disabled={presales?.status != 'canceled' ? false : true}
              >
                <Icon className="mr-2">delete</Icon>
                Cancelar orçamento
              </Button>
              <DeleteModal
                open={disableModal}
                close={() => setDisableModal(false)}
                delete={() => cancelPresale(`/presales/${presales_id}/cancel`)}
                title="Cancelar orçamento?"
                redirect={'/admin/orcamentos'}
                description="Você realmente deseja cancelar este orçamento? Essa ação é irreversível,
              mas você poderá cadastrá-lo no futuro novamente."
              />
            </div>}
          </div>

          {/* Status do orçamento interno */}
          {!approve && (
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>Status do orçamento</div>
              <div style={{ padding: 16 }}>
                <div className="flex text-base font-normal items-center" style={{ color: C.textMain }}>
                  <div className="flex">
                    {status[presales?.status_discount_approve]?.text}{' '}
                    <Icon className="ml-2">{status[presales?.status_discount_approve]?.icon}</Icon>
                  </div>
                  {presales?.status_discount_approve === 'sold' && (
                    <Link color="inherit" to={`/admin/reservas/detalhe/${presales.sale_id}`}>
                      <Button
                        textcolor="#fff"
                        padding="2px"
                        width="100px"
                        marginleft="10px"
                        margintop="0px"
                        customcolor={props.theme.color}
                      >
                        Ver venda
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Card: status do pedido gerencial (consultor) */}
          {!approve && presales?.approval_status && (
            <div style={{ ...cardStyle, border: `1px solid ${
              presales.approval_status === 'approved' ? '#22c55e' :
              presales.approval_status === 'refused'  ? '#ef4444' : '#F5C842'
            }` }}>
              <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Icon style={{ fontSize: 20, color:
                  presales.approval_status === 'approved' ? '#22c55e' :
                  presales.approval_status === 'refused'  ? '#ef4444' : '#F5C842'
                }}>
                  {presales.approval_status === 'approved' ? 'check_circle' :
                   presales.approval_status === 'refused'  ? 'cancel' : 'pending_actions'}
                </Icon>
                <span style={{ color: C.textMain, fontWeight: 700, fontSize: 14 }}>
                  {presales.approval_status === 'approved' ? 'Condição especial aprovada' :
                   presales.approval_status === 'refused'  ? 'Pedido gerencial recusado' :
                   'Pedido gerencial aguardando aprovação'}
                </span>
                <span style={{
                  marginLeft: 'auto', fontSize: 13, fontWeight: 600,
                  color: presales.approval_status === 'approved' ? '#22c55e' :
                         presales.approval_status === 'refused'  ? '#ef4444' : '#F5C842',
                }}>
                  {approvalTypeLabel(presales.approval_type,
                    presales.approval_status === 'approved' && presales.approval_manager_value
                      ? presales.approval_manager_value
                      : presales.approval_value)}
                </span>
              </div>
              {presales.approval_justify && (
                <div style={{ padding: '0 16px 12px', color: C.textSec, fontSize: 12, fontStyle: 'italic' }}>
                  "{presales.approval_justify}"
                </div>
              )}
            </div>
          )}

          {/* Informações do cliente + pedido */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>Informações do cliente</div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, color: C.textMain, fontSize: 15 }}>
                <div>{presales.name}</div>
                <div>{presales.email}</div>
                <div>Documento: {presales.document}</div>
              </div>
            </div>

            <div style={{ padding: '16px', fontSize: 18, color: C.textMain, borderTop: `1px solid ${C.hover}` }}>
              Informações do pedido
            </div>
            {budgets && (
              <div style={{ padding: 16 }}>
                <RequestTable {...props} dataBudgets={budgets} show={true} total={presales.total} urlLink={urlLink} />
              </div>
            )}
          </div>

          {/* Vendedor */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>Informações do vendedor</div>
            <div style={{ display: 'flex', padding: 16 }}>
              <div style={{ flex: 1, color: C.textSec }}>Vendedor responsável</div>
              <div style={{ flex: 1, color: C.textMain }}>
                {presales.seller_name} ({presales.seller_email})
              </div>
            </div>
          </div>

          {/* Thread de comentários */}
          {!approve && <CommentsThread presalesId={presales_id} />}

          {/* Card: detalhes do pedido gerencial (visível para o gerente) */}
          {approve && presales?.approval_status && (
            <div style={{ ...cardStyle, border: `1px solid ${
              presales.approval_status === 'approved' ? '#22c55e' :
              presales.approval_status === 'refused'  ? '#ef4444' : '#F5C842'
            }`, marginBottom: 0 }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.hover}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon style={{ fontSize: 18, color: '#F5C842' }}>pending_actions</Icon>
                <span style={{ color: C.textMain, fontWeight: 700, fontSize: 14 }}>Pedido de condição especial</span>
                <span style={{
                  marginLeft: 'auto', fontSize: 12, fontWeight: 700, padding: '2px 10px',
                  borderRadius: 20,
                  color: presales.approval_status === 'approved' ? '#22c55e' :
                         presales.approval_status === 'refused'  ? '#ef4444' : '#F5C842',
                  border: `1px solid ${presales.approval_status === 'approved' ? '#22c55e' :
                           presales.approval_status === 'refused'  ? '#ef4444' : '#F5C842'}`,
                }}>
                  {presales.approval_status === 'approved' ? 'Aprovado' :
                   presales.approval_status === 'refused'  ? 'Recusado' : 'Aguardando'}
                </span>
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: C.textSec, fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Tipo</div>
                  <div style={{ color: '#F5C842', fontWeight: 700, fontSize: 14 }}>
                    {presales.approval_type === 'discount_pct'   ? 'Desconto %' :
                     presales.approval_type === 'discount_value' ? 'Desconto R$' :
                     presales.approval_type === 'installment'    ? 'Parcelamento' : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ color: C.textSec, fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Valor solicitado</div>
                  <div style={{ color: C.textMain, fontWeight: 700, fontSize: 14 }}>
                    {approvalTypeLabel(presales.approval_type, presales.approval_value)}
                  </div>
                </div>
                {presales.approval_manager_value && (
                  <div>
                    <div style={{ color: C.textSec, fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Aprovado pelo gerente</div>
                    <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 14 }}>
                      {approvalTypeLabel(presales.approval_type, presales.approval_manager_value)}
                    </div>
                  </div>
                )}
                {presales.approval_justify && (
                  <div style={{ flexBasis: '100%' }}>
                    <div style={{ color: C.textSec, fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>Justificativa</div>
                    <div style={{ color: C.textSec, fontSize: 13, fontStyle: 'italic' }}>"{presales.approval_justify}"</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rodapé — botões de ação */}
          <div className="flex justify-end mb-5 space-x-5">
            {approve ?
              <>
                <Button
                  textcolor="#e8f0fb"
                  padding="6px 15px"
                  width="200"
                  variant="outlined"
                  bgColor="transparent"
                  onClick={() => { setRefuseNote(''); setRefuseModal(true); }}
                  disabled={presales?.approval_status !== 'pending'}
                >
                  Recusar pedido
                </Button>
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  width="200"
                  variant="outlined"
                  customcolor={props.theme.color}
                  onClick={() => {
                    setApprovalManagerValue(presales?.approval_value || '');
                    setApprovalManagerNote('');
                    setApprovalError(null);
                    setApprovalModal(true);
                  }}
                  disabled={presales?.approval_status !== 'pending'}
                >
                  Aprovar pedido
                </Button>
              </>
              :
              <>
                <Link color="inherit" to={`/voucher-presale/${presales_id}/${urlLink}`} target='_blank'>
                  <Button
                    textcolor="#fff"
                    padding="6px 15px"
                    width="200px"
                    customcolor={props.theme.color}
                    disabled={presales?.status != 'canceled' ? false : true}
                  >
                    Ver documento
                  </Button>
                </Link>
                {presales?.sale_id && (
                  <a href={`/vs/${presales.sale_id}`} target="_blank" rel="noopener noreferrer">
                    <Button
                      textcolor="#fff"
                      padding="6px 15px"
                      width="200px"
                      customcolor={props.theme.color}
                    >
                      <Icon style={{ fontSize: 16, marginRight: 6 }}>receipt_long</Icon>
                      Ver voucher
                    </Button>
                  </a>
                )}
                <Button
                  textcolor="#e8f0fb"
                  padding="6px 15px"
                  width="200px"
                  variant="outlined"
                  bgColor="transparent"
                  onClick={openSendEmailModal}
                  disabled={presales?.status === 'canceled'}
                >
                  <Icon style={{ fontSize: 16, marginRight: 6 }}>email</Icon>
                  Reenviar email
                </Button>
                <Button
                  textcolor="#fff"
                  padding="6px 15px"
                  width="200px"
                  disabled={!(presales?.status !== 'canceled' && (presales?.status_discount_approve === 'open' || presales?.status_discount_approve === 'approved'))}
                  onClick={() => saleConvert(presales_id)}
                  customcolor={props.theme.color}
                >
                  Converter em compra
                </Button>
              </>
            }
            {/* <Button
              textcolor="#e8f0fb"
              padding="6px 15px"
              width="200"
              variant="outlined"
              onClick={() => setSendVoucherModal(true)}
              bgColor="transparent"
            >
              <Icon className="mr-2">
                email
              </Icon>
              Reenviar voucher
            </Button> */}
            <SendVoucherModal
              saleId={presales_id}
              open={sendVoucherModal}
              close={() => setSendVoucherModal(false)}
              {...props}
            />
          </div>
        </div>
      </>
    }

    {/* ── Modal: reenviar email do orçamento ── */}
    {sendEmailModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#172B4D', borderRadius: 16, padding: '32px 28px',
          width: 420, maxWidth: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Icon style={{ color: '#62DEC2', fontSize: 22 }}>email</Icon>
            <span style={{ color: '#e8f0fb', fontWeight: 700, fontSize: 16 }}>
              Reenviar email do orçamento
            </span>
          </div>

          {sendEmailSuccess ? (
            <div style={{
              background: '#14532d', border: '1px solid #22c55e', borderRadius: 10,
              padding: '14px 16px', color: '#bbf7d0', textAlign: 'center', fontSize: 14,
            }}>
              ✓ Email enviado com sucesso!
            </div>
          ) : (
            <>
              <label style={{ color: '#8ba5c4', fontSize: 13, display: 'block', marginBottom: 8 }}>
                Endereço de e-mail do destinatário
              </label>
              <input
                type="email"
                value={sendEmailAddress}
                onChange={e => setSendEmailAddress(e.target.value)}
                placeholder="email@cliente.com"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: '#0f1e35', border: '1px solid #2a4f72',
                  borderRadius: 8, padding: '10px 14px',
                  color: '#e8f0fb', fontSize: 14, outline: 'none',
                  marginBottom: 20,
                }}
              />
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSendEmailModal(false)}
                  style={{
                    background: 'transparent', border: '1px solid #2a4f72',
                    borderRadius: 8, padding: '8px 20px',
                    color: '#8ba5c4', cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={!sendEmailAddress || sendEmailLoading}
                  style={{
                    background: '#62DEC2', border: 'none', borderRadius: 8,
                    padding: '8px 24px', color: '#0f1e35',
                    fontWeight: 700, cursor: sendEmailLoading ? 'not-allowed' : 'pointer',
                    fontSize: 14, opacity: (!sendEmailAddress || sendEmailLoading) ? 0.6 : 1,
                  }}
                >
                  {sendEmailLoading ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}

    {/* ── Modal: gerente aprova pedido gerencial ── */}
    {approvalModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#172B4D', borderRadius: 16, padding: '32px 28px',
          width: 440, maxWidth: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Icon style={{ color: '#62DEC2', fontSize: 22 }}>check_circle</Icon>
            <span style={{ color: '#e8f0fb', fontWeight: 700, fontSize: 16 }}>Aprovar pedido gerencial</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#8ba5c4', fontSize: 12, marginBottom: 4 }}>Tipo solicitado</div>
            <div style={{ color: '#F5C842', fontWeight: 700, fontSize: 14 }}>
              {approvalTypeLabel(presales?.approval_type, presales?.approval_value)}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#8ba5c4', fontSize: 13, display: 'block', marginBottom: 8 }}>
              {presales?.approval_type === 'discount_pct'   ? 'Valor aprovado (%)' :
               presales?.approval_type === 'discount_value' ? 'Valor aprovado (R$)' :
               'Parcelas aprovadas (x)'}
            </label>
            <input
              type="text"
              value={approvalManagerValue}
              onChange={e => setApprovalManagerValue(e.target.value)}
              placeholder={presales?.approval_type === 'installment' ? 'ex: 6' : 'ex: 10'}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f1e35', border: '1px solid #2a4f72',
                borderRadius: 8, padding: '10px 14px',
                color: '#e8f0fb', fontSize: 14, outline: 'none', marginBottom: 4,
              }}
            />
            <div style={{ color: '#8ba5c4', fontSize: 11 }}>
              Pode ajustar o valor solicitado pelo consultor antes de aprovar.
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ color: '#8ba5c4', fontSize: 13, display: 'block', marginBottom: 8 }}>
              Nota interna (opcional)
            </label>
            <textarea
              value={approvalManagerNote}
              onChange={e => setApprovalManagerNote(e.target.value)}
              placeholder="Comentário interno para o consultor..."
              rows={2}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f1e35', border: '1px solid #2a4f72',
                borderRadius: 8, padding: '10px 14px',
                color: '#e8f0fb', fontSize: 14, outline: 'none', resize: 'vertical',
              }}
            />
          </div>

          {approvalError && (
            <div style={{
              background: '#450a0a', border: '1px solid #ef4444', borderRadius: 8,
              padding: '8px 12px', color: '#fca5a5', fontSize: 13, marginBottom: 14,
            }}>
              {approvalError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setApprovalModal(false)}
              style={{
                background: 'transparent', border: '1px solid #2a4f72',
                borderRadius: 8, padding: '8px 20px',
                color: '#8ba5c4', cursor: 'pointer', fontSize: 14,
              }}
            >
              Cancelar
            </button>
            <button
              onClick={doApproveApproval}
              disabled={!approvalManagerValue.trim() || approvalSubmitting}
              style={{
                background: '#62DEC2', border: 'none', borderRadius: 8,
                padding: '8px 24px', color: '#0f1e35',
                fontWeight: 700, fontSize: 14,
                cursor: (!approvalManagerValue.trim() || approvalSubmitting) ? 'not-allowed' : 'pointer',
                opacity: (!approvalManagerValue.trim() || approvalSubmitting) ? 0.6 : 1,
              }}
            >
              {approvalSubmitting ? 'Aprovando...' : 'Confirmar aprovação'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Modal: gerente recusa pedido gerencial ── */}
    {refuseModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#172B4D', borderRadius: 16, padding: '32px 28px',
          width: 420, maxWidth: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Icon style={{ color: '#ef4444', fontSize: 22 }}>cancel</Icon>
            <span style={{ color: '#e8f0fb', fontWeight: 700, fontSize: 16 }}>Recusar pedido gerencial</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#8ba5c4', fontSize: 12, marginBottom: 4 }}>Pedido</div>
            <div style={{ color: '#F5C842', fontWeight: 700, fontSize: 14 }}>
              {approvalTypeLabel(presales?.approval_type, presales?.approval_value)}
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ color: '#8ba5c4', fontSize: 13, display: 'block', marginBottom: 8 }}>
              Motivo da recusa (opcional — só o consultor verá)
            </label>
            <textarea
              value={refuseNote}
              onChange={e => setRefuseNote(e.target.value)}
              placeholder="Ex: desconto acima do limite permitido para a região"
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f1e35', border: '1px solid #2a4f72',
                borderRadius: 8, padding: '10px 14px',
                color: '#e8f0fb', fontSize: 14, outline: 'none', resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setRefuseModal(false)}
              style={{
                background: 'transparent', border: '1px solid #2a4f72',
                borderRadius: 8, padding: '8px 20px',
                color: '#8ba5c4', cursor: 'pointer', fontSize: 14,
              }}
            >
              Cancelar
            </button>
            <button
              onClick={doRefuseApproval}
              disabled={approvalSubmitting}
              style={{
                background: '#ef4444', border: 'none', borderRadius: 8,
                padding: '8px 24px', color: '#fff',
                fontWeight: 700, fontSize: 14,
                cursor: approvalSubmitting ? 'not-allowed' : 'pointer',
                opacity: approvalSubmitting ? 0.6 : 1,
              }}
            >
              {approvalSubmitting ? 'Recusando...' : 'Confirmar recusa'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Modal: datas com problema antes de converter ── */}
    {dateCheckModal && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}>
        <div style={{
          background: '#172B4D',
          borderRadius: 14,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 520,
          maxHeight: '85vh',
          overflowY: 'auto',
          color: '#e8f0fb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Icon style={{ color: '#fb923c', fontSize: 22 }}>warning</Icon>
            <span style={{ fontSize: 17, fontWeight: 700 }}>Confirmar datas antes de converter</span>
          </div>
          <p style={{ color: '#8ba5c4', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
            Os passeios abaixo não têm data definida ou a data escolhida não tem vagas suficientes.
            Selecione uma nova data para cada um antes de continuar.
          </p>

          {dateIssues.map(issue => {
            const isTransfer = !!issue.is_transfer;

            if (isTransfer) {
              const currentDate = fixDates[issue.budget_id] || '';
              const isValid = !!currentDate;
              return (
                <div key={issue.budget_id} style={{
                  background: '#0f1c2e',
                  borderRadius: 10,
                  padding: '14px 16px',
                  marginBottom: 14,
                  border: `1px solid ${isValid ? '#14432a' : '#3d2a0a'}`,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                    ✈️ {issue.tour_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#8ba5c4', marginBottom: 10 }}>
                    ⚠️ Sem data definida
                  </div>
                  <input
                    type="date"
                    value={currentDate}
                    onChange={e => setFixDates(prev => ({ ...prev, [issue.budget_id]: e.target.value }))}
                    style={{
                      width: '100%',
                      background: '#172B4D',
                      color: '#e8f0fb',
                      border: `1px solid ${isValid ? '#62DEC2' : '#2a4f72'}`,
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: 13,
                      boxSizing: 'border-box',
                      marginBottom: 8,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <input
                      type="text"
                      placeholder="Código do voo (ex: LA123)"
                      value={fixFlightCodes[issue.budget_id] || ''}
                      onChange={e => setFixFlightCodes(prev => ({ ...prev, [issue.budget_id]: e.target.value }))}
                      style={{
                        flex: 1,
                        background: '#172B4D',
                        color: '#e8f0fb',
                        border: '1px solid #2a4f72',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 13,
                      }}
                    />
                    <input
                      type="time"
                      value={fixFlightTimes[issue.budget_id] || ''}
                      onChange={e => setFixFlightTimes(prev => ({ ...prev, [issue.budget_id]: e.target.value }))}
                      style={{
                        flex: 1,
                        background: '#172B4D',
                        color: '#e8f0fb',
                        border: '1px solid #2a4f72',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 13,
                      }}
                    />
                  </div>
                  {isValid && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#62DEC2' }}>✓ Data definida</div>
                  )}
                </div>
              );
            }

            // Tour / combo
            const availList = fixAvailDates[issue.budget_id] || [];
            const currentDate = fixDates[issue.budget_id] || '';
            const isValid = currentDate && availList.some(
              d => d.cupo_date === currentDate && d.total_cupos >= issue.cupos_needed
            );
            return (
              <div key={issue.budget_id} style={{
                background: '#0f1c2e',
                borderRadius: 10,
                padding: '14px 16px',
                marginBottom: 14,
                border: `1px solid ${isValid ? '#14432a' : '#3d2a0a'}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                  {issue.tour_name}
                </div>
                <div style={{ fontSize: 12, color: '#8ba5c4', marginBottom: 10 }}>
                  {issue.issue === 'no_date'
                    ? '⚠️ Sem data definida'
                    : `⚠️ Data ${issue.tour_date ? new Date(`${issue.tour_date}T00:00:00`).toLocaleDateString('pt-BR') : '—'} — ${issue.cupos_available} vaga${issue.cupos_available !== 1 ? 's' : ''} disponível (necessário: ${issue.cupos_needed})`
                  }
                </div>
                <select
                  value={currentDate}
                  onChange={e => setFixDates(prev => ({ ...prev, [issue.budget_id]: e.target.value }))}
                  style={{
                    width: '100%',
                    background: '#172B4D',
                    color: '#e8f0fb',
                    border: `1px solid ${isValid ? '#62DEC2' : '#2a4f72'}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 13,
                  }}
                >
                  <option value="">— Selecione uma data —</option>
                  {availList
                    .filter(d => d.total_cupos >= issue.cupos_needed)
                    .sort((a, b) => a.cupo_date.localeCompare(b.cupo_date))
                    .map(d => (
                      <option key={d.cupo_date} value={d.cupo_date}>
                        {new Date(`${d.cupo_date}T00:00:00`).toLocaleDateString('pt-BR')} — {d.total_cupos} vagas
                      </option>
                    ))
                  }
                  {issue.tour_date && !availList.some(d => d.cupo_date === issue.tour_date && d.total_cupos >= issue.cupos_needed) && (
                    <option value={issue.tour_date} disabled style={{ color: '#8ba5c4' }}>
                      {new Date(`${issue.tour_date}T00:00:00`).toLocaleDateString('pt-BR')} — data sugerida (sem vagas)
                    </option>
                  )}
                </select>
                {isValid && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#62DEC2' }}>✓ Data válida selecionada</div>
                )}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              onClick={() => { setDateCheckModal(false); setConverting(false); }}
              style={{
                flex: 1, background: 'transparent', border: '1px solid #2a4f72',
                color: '#8ba5c4', borderRadius: 8, padding: '10px 0',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Cancelar
            </button>
            <button
              onClick={saveFixedDatesAndConvert}
              disabled={converting || dateIssues.some(i => {
                const d = fixDates[i.budget_id];
                if (i.is_transfer) return !d;
                const avail = fixAvailDates[i.budget_id] || [];
                return !d || !avail.some(a => a.cupo_date === d && a.total_cupos >= i.cupos_needed);
              })}
              style={{
                flex: 2,
                background: converting ? '#2a4f72' : props.theme?.color || '#62DEC2',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 0',
                cursor: converting ? 'default' : 'pointer',
                fontSize: 13,
                fontWeight: 700,
                opacity: dateIssues.some(i => {
                  const d = fixDates[i.budget_id];
                  if (i.is_transfer) return !d;
                  const avail = fixAvailDates[i.budget_id] || [];
                  return !d || !avail.some(a => a.cupo_date === d && a.total_cupos >= i.cupos_needed);
                }) ? 0.5 : 1,
              }}
            >
              {converting ? 'Convertendo...' : 'Salvar datas e converter'}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
}

export default Show;
